import React, {useEffect, useMemo, useState} from 'react'
import Tr from 'translations/Translations';
import Dialog from 'components/Dialog';
import GridBlock from 'components/GridBlock';
import Form from 'components/Form';
import * as Yup from 'yup';
import useId from 'hooks/useId';
import {connect} from 'react-redux'
import {makeStyles} from "@material-ui/core";
import DoubleButton from "components/popup/components/DoubleButton";
import DateTimePicker from "components/DateTimePicker";
import Select from "components/Select";
import DescriptionIcon from '@material-ui/icons/Description';
import FolderIcon from '@material-ui/icons/Folder';
import TypographyWithIcon from "components/TypographyWithIcon";
import moment from "libs/moment";
import VS from "constants/ValidationShema";
import RepoService from "services/RepoService";
import ContractorSelect from "../../../ContractorSelect";
import Loading from "../../../Loading";

const useStyle = makeStyles(theme => ({
    margin: {
        marginBottom: theme.spacing(2)
    }
}));

function ChangeRepoVisibilityPopup(
    {
        onClose = () => {
        },
        demandId,
        repoVisibility = {},
        formSubmit,
        repoVisibilityTypes
    }) {

    const {fileId, folderId, name, visibilityStatus, visibilityEndDate, contractors = []} = repoVisibility;
    const isFile = !!fileId;
    const classes = useStyle();
    const formId = useId();
    const handleSubmit = () => formSubmit[formId]();

    const [visibilityTypes, setVisibilityTypes] = useState([]);
    const [allContractorsSelect, setAllContractorsSelect] = useState([]);
    const [contractorOptions, setContractorOptions] = useState([]);
    const [contractorsLoading, setContractorsLoading] = useState(false);
    const memoizedContractors = useMemo(() => contractors, [contractors]);

    useEffect(() => {
        const types = Object.keys(repoVisibilityTypes || {}).map(x => ({
            label: repoVisibilityTypes[x],
            value: x
        }));
        setVisibilityTypes(types);
    }, [repoVisibilityTypes])

    useEffect(() => {
        if (!memoizedContractors) return;

        const options = memoizedContractors.map(c => c.identity.toString());
        setContractorOptions(options);
    }, [memoizedContractors]);

    useEffect(() => {
        setContractorsLoading(true);
        RepoService.getContractors(demandId)
            .then(contractorsResult => {
                const allContractors = contractorsResult.map(x => ({
                    label: x.name,
                    value: x.identity.toString()
                }));
                setAllContractorsSelect(allContractors);
            })
            .catch(error => {
                console.error("Error while getting contractors:", error);
                setAllContractorsSelect([]);
            })
            .finally(() => setContractorsLoading(false));

        return () => {
            RepoService.clearResources(RepoService.REPO_CONTRACTORS_RESOURCE_ID);
        };
    }, [demandId]);


    const onSubmit = (values) => {

        const processedContractors = Array.isArray(values.contractors)
            ? values.contractors.map(c => {
                if (typeof c === 'object' && c !== null) {
                    return c.value || c.identity;
                }
                return c;
            })
            : [];
        const contractorsList = {
            ...values,
            contractors: processedContractors.filter(Boolean) // usuwa null-e
        };


        let promise;
        if (isFile) {
            promise = RepoService.changeFileVisibility(demandId, folderId, fileId, contractorsList);
        } else {
            promise = RepoService.changeFolderFilesVisibility(demandId, folderId, contractorsList);
        }
        promise.then(onClose);

    }

    const validationSchema = VS.lazy((value) => {

        let objShape = {
            visibility: VS.string(),
        }

        if (value.visibility !== 'STATUS_INTERNAL') {
            objShape['visibilityEndDate'] = VS.dateTimeNoReq()
        }

        if (value.visibility === 'STATUS_CHOOSE_CONTRACTOR') {
            objShape['contractors'] = Yup.array()
                .min(1, Tr('at_least_one_contractor_must_be_selected'))
                .test(
                    'contractors-required',
                    Tr('contractors_are_required'),
                    (value) => value && value.length > 0
                );
        }

        return Yup.object().shape(objShape)
    })

    return (
        <Dialog
            title={isFile ? Tr('change_file_visibility') : Tr('change_visibility_for_all_files')}
            open={true}
            maxWidth={'md'}
            fullWidth={true}
            dialogActions={<DoubleButton
                justify='flex-end'
                labelFirst={Tr('cancel')}
                labelSecond={Tr('confirm')}
                onClickFirst={onClose}
                onClickSecond={handleSubmit}
            />}
        >

            <Form
                initialValues={{
                    visibility: visibilityStatus,
                    visibilityEndDate: visibilityEndDate ? moment(visibilityEndDate) : null,
                    contractors: contractorOptions || []
                }}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                id={formId}
            >
                {(formikProps) => (
                    <GridBlock column>

                        <TypographyWithIcon Icon={isFile ? DescriptionIcon : FolderIcon} className={classes.margin}>
                            {name}
                        </TypographyWithIcon>

                        <Select
                            id='visibility'
                            formikProps={formikProps}
                            items={visibilityTypes}
                            label={Tr('repo_visibility_type')}
                        />

                        {formikProps.values.visibility === 'STATUS_CHOOSE_CONTRACTOR' && (
                            contractorsLoading ? (
                                <Loading />
                            ) : (
                                <ContractorSelect
                                    id='contractors'
                                    formikProps={formikProps}
                                    items={allContractorsSelect}
                                    label={Tr('repo_select_contractors')}
                                    className={classes.margin}
                                    selected={contractorOptions}
                                />
                            )
                        )}
                        <DateTimePicker
                            formikProps={formikProps}
                            id='visibilityEndDate'
                            label={Tr('repo_visibility_end_date')}
                            disabled={formikProps.values.visibility === 'STATUS_INTERNAL'}
                        />
                    </GridBlock>
                )}
            </Form>
        </Dialog>
    )
}

export default connect((state) => ({
    formSubmit: state.form,
    repoVisibilityTypes: state.resources.repoVisibilityTypes
}), {})(ChangeRepoVisibilityPopup);